// Transfert automatique du paiement d'inscription — à la PREMIÈRE CONNEXION.
//
// Contexte (règle métier IMMO BF) : le client choisit sa formule payante et
// joint sa preuve UNE SEULE FOIS, dans le formulaire `/inscription`. La
// confirmation d'e-mail étant active, `signUp` ne renvoie AUCUNE session : la
// preuve ne peut donc pas être téléversée au moment du clic (un upload anonyme
// serait refusé par RLS — et l'autoriser serait une faille).
//
// Solution : la preuve est conservée temporairement côté navigateur (Cache
// Storage). Dès que le client possède une session authentifiée (première
// connexion), on envoie AUTOMATIQUEMENT la demande de paiement — le client ne
// re-fournit jamais rien.
//
// Le serveur reste seul juge : `soumettre_paiement_inscription` déduit le compte
// de la session (auth.uid()), recalcule le montant officiel et applique le verrou
// d'idempotence + `uniq_paiement_abonnement_en_attente`. Aucun montant, aucun
// compte_id, aucune formule non autorisée ne provient du navigateur.
//
// Aucune donnée fictive. Aucun secret. Le bucket de destination reste privé.

import { getSafeUser } from "@/store/shared/supabase";
import { getFormuleConfig, type FormuleId, type Periodicite } from "@/config/formules";
import { getMethodePaiement, type PaiementMethode } from "@/config/paiement";
import { soumettrePaiementInscriptionRpc } from "./paiementAbonnement";
import { uploadPreuve } from "@/store/shared/preuves";
import { readPendingPreuve, clearPendingPreuve } from "@/store/shared/preuvesPending";
import { fetchMonAbonnement, demandePaiementFromRpc, joindrePreuveAbonnementRpc } from "./abonnement";

const FORMULE_IDS: FormuleId[] = ["gratuit", "essentiel", "professionnel", "agence"];

export interface InscriptionIntent {
  formule: FormuleId;
  periodicite: Periodicite;
  mode: PaiementMethode | "";
  reference: string;
  /** Formule payante (prix mensuel > 0) : une demande de paiement est requise. */
  payant: boolean;
}

/**
 * Relit l'intention de paiement enregistrée dans les métadonnées du compte
 * (formule, périodicité, moyen, référence). Retourne null si aucune intention
 * payante exploitable n'existe.
 */
export function readInscriptionIntent(meta: Record<string, unknown>): InscriptionIntent | null {
  const formule = typeof meta.formule === "string" && (FORMULE_IDS as string[]).includes(meta.formule)
    ? (meta.formule as FormuleId)
    : null;
  if (!formule) return null;

  const config = getFormuleConfig(formule);
  const payant = Boolean(config && config.prixMensuel > 0);
  if (!payant) return null;

  const periodicite: Periodicite = meta.periodicite === "annuel" ? "annuel" : "mensuel";
  const mode: PaiementMethode | "" =
    meta.mode_paiement === "orange_money" ||
    meta.mode_paiement === "moov_money" ||
    meta.mode_paiement === "especes"
      ? meta.mode_paiement
      : "";
  const reference = typeof meta.reference === "string" ? meta.reference : "";

  return { formule, periodicite, mode, reference, payant };
}

export type FinalisationResultat = "rien" | "deja" | "preuve-manquante" | "envoye" | "echec";

// Anti-double exécution : une seule tentative en vol par utilisateur, et le
// succès (ou l'existence d'une demande) est mémorisé pour la session courante.
let enVol: Promise<FinalisationResultat> | null = null;
const dejaTraite = new Set<string>();

/**
 * Finalise la demande de paiement d'inscription si nécessaire.
 *
 * Renvoie :
 *  - "rien"              : pas d'intention payante (rien à faire) ;
 *  - "deja"             : une demande (en attente/validée) existe déjà ;
 *  - "preuve-manquante" : la preuve conservée n'est plus disponible (autre
 *                         appareil/navigateur) — le client devra la rejoindre ;
 *  - "envoye"           : la demande vient d'être transmise au serveur ;
 *  - "echec"            : la transmission a échoué (repli possible côté client).
 */
export async function finaliserInscriptionSiBesoin(): Promise<FinalisationResultat> {
  if (enVol) return enVol;
  enVol = (async (): Promise<FinalisationResultat> => {
    try {
      const user = await getSafeUser();
      if (!user) return "rien";
      if (dejaTraite.has(user.id)) return "deja";

      const intent = readInscriptionIntent((user.user_metadata ?? {}) as Record<string, unknown>);
      if (!intent || !intent.mode) return "rien";

      // Une demande validée ne doit jamais être recréée ni modifiée.
      const abonnement = await fetchMonAbonnement();
      const demande = demandePaiementFromRpc(abonnement);
      if (demande && demande.statut === "valide") {
        dejaTraite.add(user.id);
        return "deja";
      }

      // Une demande déjà en attente ne doit jamais être recréée. Si elle attend
      // encore sa preuve (demande récupérée sans preuve), on rattache la preuve
      // conservée dans le navigateur — sans aucune ressaisie ni nouveau paiement.
      if (demande && demande.statut === "en_attente") {
        const besoin = getMethodePaiement(demande.modePaiement as PaiementMethode).preuveObligatoire;
        if (besoin && !demande.preuveUrl) {
          const email = user.email ?? "";
          const preuveEnCache = await readPendingPreuve(email);
          if (preuveEnCache) {
            const preuvePath = await uploadPreuve(preuveEnCache);
            await joindrePreuveAbonnementRpc(preuvePath);
            await clearPendingPreuve(email);
            dejaTraite.add(user.id);
            return "envoye";
          }
        }
        dejaTraite.add(user.id);
        return "deja";
      }

      const besoinPreuve = getMethodePaiement(intent.mode).preuveObligatoire;
      const email = user.email ?? "";
      const preuveEnCache = besoinPreuve ? await readPendingPreuve(email) : null;
      if (besoinPreuve && !preuveEnCache) {
        return "preuve-manquante";
      }

      let preuvePath = "";
      if (besoinPreuve && preuveEnCache) {
        preuvePath = await uploadPreuve(preuveEnCache);
      }

      await soumettrePaiementInscriptionRpc({
        formuleCode: intent.formule,
        periodicite: intent.periodicite,
        methode: intent.mode,
        reference: besoinPreuve ? intent.reference : "",
        preuveUrl: preuvePath,
      });

      if (besoinPreuve) await clearPendingPreuve(email);
      dejaTraite.add(user.id);
      return "envoye";
    } catch {
      return "echec";
    } finally {
      enVol = null;
    }
  })();
  return enVol;
}